import request from 'request-promise-native';
import * as utils from '@/lib/utils';
import { getInstance } from '@/plugins/Auth0Lock';
import MockHipApi from './__mocks__/HipApi';
import Config from '../config';

const apiHost = Config.value('VUE_APP_HIP_API_HOST');
const apikey = Config.value('VUE_APP_BACKEND_API_KEY') || '';

export default class HipAPI {
    public isDemo = false;
    private MockHipApi = MockHipApi.getInstance();

    private static instance: HipAPI;

    private constructor() {
    }

    public static getInstance() {
        if (!HipAPI.instance) {
            HipAPI.instance = new HipAPI();
        }
        return HipAPI.instance;
    }

    apiGetRequest = async (endpoint: string) => {
        const token = await getInstance().getTokenSilently();
        const options = {
            url: `${apiHost}/api/v1/corporates${endpoint}`,
            json: true,
            headers: {
                ApiKey: apikey,
                Authorization: `Bearer ${token}`,
            },
        };

        return request(options);
    };

    apiPostRequest = async (endpoint: string, postBody: object = {}) => {
        const token = await getInstance().getTokenSilently();
        const options = {
            method: 'POST',
            uri: `${apiHost}/api/v1/corporates${endpoint}`,
            body: postBody,
            json: true,
            headers: {
                ApiKey: apikey,
                Authorization: `Bearer ${token}`,
            },
        };

        return request(options);
    };

    // ##########################
    // Rides
    // ##########################

    getRides() {
        return this.apiGetRequest('/rides?enrichData=true');
    }

    getRidesAtDate(date: string) {
        if (this.isDemo) {
            return this.MockHipApi.getRidesAtDate();
        }
        return this.apiGetRequest(`/rides?date=${date}&enrichData=true`);
    }

    getOngoingRides() {
        if (this.isDemo) {
            return this.MockHipApi.getOngoingRides();
        }
        return this.apiGetRequest('/rides/ongoing');
    }

    getRidesForNextWeek() {
        const fromDate = utils.today();
        const toDate = utils.incrementDateByDays(7, fromDate);
        return this.apiGetRequest(`/rides?fromDate=${fromDate}&toDate=${toDate}&enrichData=true`);
    }

    getRidesForDate(date: string, enrichData = true) {
        return this.apiGetRequest(`/rides?date=${date}&enrichData=${enrichData}`);
    }

    getRidesFromDates(fromDate: string, toDate: string) {
        return this.apiGetRequest(`/rides?fromDate=${fromDate}&toDate=${toDate}&enrichData=true`);
    }

    getPassengersList(rideId: number) {
        return this.apiGetRequest(`/rides/${rideId}/passengers`);
    }

    getRideGeojson(rideId: number) {
        return this.apiGetRequest(`/rides/${rideId}/geojson`);
    }

    getRideSustainabilityDataFromDates(fromDate: string, toDate: string) {
        return this.apiGetRequest(`/rides/sustainabilityData?fromDate=${fromDate}&toDate=${toDate}`);
    }

    // ##########################
    // Users
    // ##########################

    getUsers() {
        return this.apiGetRequest('/users');
    }

    addUser(usersData: object) {
        return this.apiPostRequest('/users', usersData);
    }

    addRegisteredUser(usersData: object) {
        return this.apiPostRequest('/users/registered', usersData);
    }

    editUser(corpUserId: number, usersData: object) {
        return this.apiPostRequest(`/users/${corpUserId}`, usersData);
    }

    getUserTickets(userId: number, fromDate: string, toDate: string) {
        return this.apiGetRequest(`/users/${userId}/tickets?fromDate=${fromDate}&toDate=${toDate}`);
    }

    getUserSharedRides(userId: number, fromDate: string, toDate: string) {
        return this.apiGetRequest(`/users/${userId}/tickets/shared?fromDate=${fromDate}&toDate=${toDate}`);
    }

    cancelTicket(userId: number, ticketId: number) {
        return this.apiPostRequest(`/users/${userId}/tickets/${ticketId}/cancel`);
    }

    markTicketAsUsed(userId: number, ticketId: number) {
        return this.apiPostRequest(`/users/${userId}/tickets/${ticketId}/board`);
    }

    getAvailableAreas(userId: number) {
        return this.apiGetRequest(`/users/${userId}/tickets/available/areas`);
    }

    getAvailableDates(userId: number, isTwoWay: boolean, pickupAreaId: number, dropoffAreaId: number) {
        return this.apiGetRequest(`/users/${userId}/tickets/available/dates?twoWay=${isTwoWay}&pickupAreaId=${pickupAreaId}&dropoffAreaId=${dropoffAreaId}`);
    }

    getAvailableStops(userId: number, date: string, isTwoWay: boolean, pickupAreaId: number, dropoffAreaId: number) {
        return this.apiGetRequest(`/users/${userId}/tickets/available/stops?date=${date}&twoWay=${isTwoWay}&pickupAreaId=${pickupAreaId}&dropoffAreaId=${dropoffAreaId}`);
    }

    getAvailableSchedules(userId: number, date: string, boardingStopId: number, alightingStopId: number, isTwoWay: boolean) {
        return this.apiGetRequest(`/users/${userId}/tickets/available/schedules?date=${date}&boardingStopId=${boardingStopId}&alightingStopId=${alightingStopId}&twoWay=${isTwoWay}`);
    }

    addUserTicket(userId: number, ticketData: object) {
        return this.apiPostRequest(`/users/${userId}/bookings`, ticketData);
    }

    editUserTicket(userId: number, ticketId: number, ticketData: object) {
        return this.apiPostRequest(`/users/${userId}/tickets/${ticketId}`, ticketData);
    }
    // user credit
    addCreditToUser(data: any) {
        return this.apiPostRequest(`/users/${data.userId}/credit`, data);
    }

    getCreditTransaction(userId: number, fromDate: string, toDate: string) {
        return this.apiGetRequest(`/users/${userId}/credit/transactions?fromDate=${fromDate}&toDate=${toDate}`);
    }

    getCreditBalance(userId: number) {
        return this.apiGetRequest(`/users/${userId}/credit/balance`);
    }
    // user payment
    getUserPayments(userId: number, fromDate: string, toDate: string) {
        return this.apiGetRequest(`/users/${userId}/payments/history?fromDate=${fromDate}&toDate=${toDate}`);
    }

    // ##########################
    // Offices
    // ##########################

    getCorpOffice() {
        return this.apiGetRequest('/offices');
    }
    getAllCorpOffices() {
        if (this.isDemo) {
            return this.MockHipApi.getAllCorpOffices();
        }
        return this.apiGetRequest('/offices?all=true');
    }

    getCorpDetails() {
        return this.apiGetRequest('/details');
    }

    // ##########################
    // Reports
    // ##########################

    getTicketsReport(fromDate: string, toDate: string) {
        return this.apiGetRequest(`/reports/tickets?fromDate=${fromDate}&toDate=${toDate}`);
    }

    getTicketsHistoryReport(fromDate: string, toDate: string) {
        return this.apiGetRequest(`/reports/tickets/history?fromDate=${fromDate}&toDate=${toDate}`);
    }

    // ##########################
    // Texts
    // ##########################

    getTexts() {
        return this.apiGetRequest('/languages/strings');
    }

    // ##########################
    // Corporations
    // ##########################

    getCorpSettings() {
        return this.apiGetRequest('/settings');
    }

    getCorpToast() {
        return this.apiGetRequest('/toasts');
    }

    addToast(formData: object) {
        return this.apiPostRequest('/toasts', formData);
    }
    editToast(toastId: number, formData: object) {
        return this.apiPostRequest(`/toasts/${toastId}`, formData);
    }

    getRatings(fromDate: string, toDate: string) {
        return this.apiGetRequest(`/ratings?fromDate=${fromDate}&toDate=${toDate}`);
    }

    // ##########################
    // Notifications
    // ##########################
    sendRideNotification(rideId: number, title: string, message: string) {
        return this.apiPostRequest(`/notifications/ride/${rideId}`, { title, message });

    }

    getProperties() {
        return this.apiGetRequest('/properties');
    }

    // ##########################
    // Tenants
    // ##########################
    getTenants() {
        return this.apiGetRequest('/tenants');
    }
}
